import { Trans, useLingui } from "@lingui/react/macro";
import styles from "./Header.module.scss";
import Person from "@/components/Person/Person";
import { LoadingOverlay } from "@medoma/components/Loading/Loading";
import DropdownMenu from "@/components/DropdownMenu/DropdownMenu";
import LogoutIcon from "@medoma/components/icons/SVG/logout.svg?react";
import { Link, useLocation, useNavigate } from "react-router";
import LogoIconOnly from "@medoma/components/icons/LogoIconOnly";
import { useCurrentEmployee } from "@/api/Employee";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useEmployeeUnreadNotificationCount } from "@medoma/chat/useUnreadNotificationCounts";
import { UnstyledLink } from "@medoma/components/Link/Link";
import { NotificationCircle } from "@medoma/components/NotificationCircle/NotificationCircle";
import ChatConversationIcon from "@medoma/components/icons/ChatConversation";
import { IconButton } from "@medoma/components/Button/Button";
import CaretLeftIcon from "@medoma/components/icons/CaretLeftIcon";
import { type IEmployee } from "@medoma/models/shifts";
import { useAuth } from "react-oidc-context";
import { IntercomButton } from "@medoma/support/IntercomButton";
import { shutdownIntercom } from "@medoma/support/shutdownIntercom";
import { clearUserSessionStorage } from "@medoma/shared-employee/userSessionStorage";
import { useUserProfile } from "@/api/Users";
import { CHAT_ENVIRONMENT, ENVIRONMENT } from "@/Utils/EnvUtils";
import { FROM_LOGIN } from "../login/IdsLogin";

const getParentRoute = (pathname: string) =>
  pathname.split("/").slice(0, -1).join("/");

const NavigateBack = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  return (
    <div className={styles.navigateBackButtonWrapper}>
      <IconButton
        aria-label={t`Gå tillbaka`}
        onClick={() => {
          // If we navigate back we end up on the PIN page. This is a bad user experience. Thus special handling.
          if (state === FROM_LOGIN) {
            void navigate(getParentRoute(pathname), {
              // Avoid adding this type of navigation to the history stack
              // This could lead to a loop where we go "back" to the parent route
              // and then back to the child route (using -1-navigation), and on and on
              replace: true,
              // We are still in "special navigation"-mode after one of these "back" navigations, so forward the FROM_LOGIN state.
              state: FROM_LOGIN,
            });
          } else {
            void navigate(-1);
          }
        }}
      >
        <CaretLeftIcon />
      </IconButton>
    </div>
  );
};

const ChatLink = () => {
  const { employeeNumber, organization } = useUserProfile();
  useEffect(() => {
    Sentry.setUser({
      username: `${ENVIRONMENT === "local" ? "dev" : ENVIRONMENT}-${organization}-${employeeNumber}`,
    });
  }, [organization, employeeNumber]);

  const numberOfUnreadMessages = useEmployeeUnreadNotificationCount({
    employeeId: employeeNumber,
    env: CHAT_ENVIRONMENT,
    organization,
  });

  const hasCompletedInitialSync = numberOfUnreadMessages !== undefined;

  const hasUnreadChatMessages =
    hasCompletedInitialSync && numberOfUnreadMessages > 0;

  return (
    <UnstyledLink to="chat">
      <div className={styles.iconLink}>
        <LoadingOverlay show={!hasCompletedInitialSync}>
          <NotificationCircle
            placement="top-left"
            isVisible={hasUnreadChatMessages}
            number={numberOfUnreadMessages}
          >
            <ChatConversationIcon />
          </NotificationCircle>
        </LoadingOverlay>
      </div>
    </UnstyledLink>
  );
};

const CustomTrigger = ({
  givenName,
  employee,
}: {
  givenName: string;
  employee: IEmployee | undefined;
}) => <Person name={givenName} photoUrl={employee?.imageURL ?? null} />;

const Header = () => {
  const { t } = useLingui();
  const { givenName } = useUserProfile();
  const auth = useAuth();

  // Employees might share computers and OS accounts, so signing out has to
  // leave the tab with nothing of this employee's in it. Both teardowns run
  // here rather than in an unmount cleanup: the messenger is booted from
  // IntercomProvider's render body, so it is up even when the effect returned
  // early on a pending token and left no cleanup behind, and the tab-scoped
  // storage has no component to hang a cleanup on at all.
  const signOut = () => {
    shutdownIntercom();
    clearUserSessionStorage();
    void auth.signoutRedirect({
      post_logout_redirect_uri: window.location.origin,
    });
  };

  const { data: employee } = useCurrentEmployee();
  const { pathname } = useLocation();
  const isOnAmbulatingHome = pathname === "/routesAndActivities";

  return (
    <header className={styles.header}>
      <Link
        aria-label={t`Startsida`}
        to="/routesAndActivities"
        className={styles.logo}
      >
        <LogoIconOnly />
      </Link>
      {isOnAmbulatingHome ? <div /> : <NavigateBack />}
      <div className={styles.alignRight}>
        <div className={styles.communicationIcons}>
          <ChatLink />
          <IntercomButton />
        </div>
        <DropdownMenu
          trigger={{
            custom: <CustomTrigger givenName={givenName} employee={employee} />,
            ariaLabel: t`Meny`,
          }}
        >
          <DropdownMenu.Item
            action={signOut}
            content={
              <>
                <LogoutIcon /> <Trans>Logga ut</Trans>
              </>
            }
          />
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
